import React, { type PropsWithChildren, type ReactNode } from 'react';
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';
import { Container as GarnishContainer } from '@garnish/core';
import { IconLink } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

// Doesn't scroll on its own, so consumers wrap it in their own scroll view.
export const Container = (props: Props) => {
  const { children, style, contentStyle, footer, onClose, removeSpacing = false } = props;

  const { currentBreakpoint } = useResponsive();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldRenderCloseButton = currentBreakpoint.isXS && onClose !== undefined;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    currentBreakpoint.isXS ? undefined : styles.containerSM,
    contentStyle,
  ];
  const contentContainerStyles = [
    styles.contentContainer,
    currentBreakpoint.isXS ? undefined : styles.contentContainerSM,
    style,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <View style={contentContainerStyles}>
        {shouldRenderCloseButton ? (
          <GarnishContainer style={styles.closeButtonContainer}>
            <IconLink name="IconClose" onPress={onClose} />
          </GarnishContainer>
        ) : null}

        <GarnishContainer removeSpacing={removeSpacing} size="medium">
          {children}
        </GarnishContainer>

        {footer}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  containerSM: {
    paddingVertical: theme.spacing['20'],
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: theme.spacing['6'],
  },
  contentContainerSM: {
    justifyContent: 'center',
  },
  closeButtonContainer: {
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'flex-end',
    marginBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  removeSpacing?: boolean;
  footer?: ReactNode;
  onClose?: () => void;
}>;
